import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    History, Loader2, RefreshCw, Package, Calendar, Search, Filter, 
    ChevronDown, ChevronRight, Layers, List, BarChart3, FileText, XCircle, Trash2 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const ConsoDepHistorique = () => {
    const [consommations, setConsommations] = useState([]);
    const [detailsMap, setDetailsMap] = useState({}); // Pour stocker les lignes de chaque fiche ouverte
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatut, setFilterStatut] = useState('TOUS'); // TOUS, VALIDE, ANNULE
    const [activeTab, setActiveTab] = useState('HISTORIQUE'); // 'HISTORIQUE' ou 'RAPPORT'
    const [expandedConso, setExpandedConso] = useState({});
    
    // Filtres par date pour le rapport
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        onConfirm: null
    });

    const primaryColor = '#000000'; // Remplacement du bordeaux par le noir
    const borderColor = '#e2e8f0';

    const fetchConsommations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get('/consommations-departements');
            setConsommations(res.data || []);
        } catch (err) {
            console.error("Erreur fetch consommations départements:", err);
            showToast("Erreur lors de la récupération de l'historique.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchTerm('');
        setFilterStatut('TOUS');
        setDateDebut('');
        setDateFin('');
    };

    // --- SOCKET.IO ---
    useEffect(() => {
        fetchConsommations();

        if (!socket) return;

        const user = JSON.parse(localStorage.getItem('user'));
        const companyId = user?.company_id || user?.companyId;
        if (companyId) {
            socket.emit('join_company', companyId.toString());
        }

        const handleRefresh = () => {
            fetchConsommations();
        };

        socket.on('REFRESH_UI', handleRefresh);
        socket.on('STOCKS_UPDATED', handleRefresh);

        return () => {
            socket.off('REFRESH_UI', handleRefresh);
            socket.off('STOCKS_UPDATED', handleRefresh);
        };
    }, [fetchConsommations]);

    const toggleExpand = async (consoId) => {
        const willExpand = !expandedConso[consoId];
        setExpandedConso(prev => ({ ...prev, [consoId]: willExpand }));

        if (willExpand && !detailsMap[consoId]) {
            try {
                const res = await API.get(`/consommations-departements/details/${consoId}`);
                setDetailsMap(prev => ({ ...prev, [consoId]: res.data?.lignes || [] }));
            } catch (err) {
                showToast("Erreur chargement des lignes de détail", "error");
            }
        }
    };

    // --- ANNULATION GLOBALE (EN-TÊTE) ---
    const handleAnnulerConso = (consoId, libelle) => {
        showConfirm(
            `Voulez-vous vraiment annuler toute la consommation "${libelle}" ? Les stocks de tous les articles seront restaurés.`,
            async () => {
                try {
                    await API.put(`/consommations-departements/annuler/${consoId}`);
                    showToast("Consommation annulée avec succès et stocks restaurés.");
                    fetchConsommations();
                    // Nettoyer les détails mis en cache pour forcer le rechargement
                    setDetailsMap(prev => {
                        const copy = { ...prev };
                        delete copy[consoId];
                        return copy;
                    });
                } catch (err) {
                    showToast(err.response?.data?.error || "Erreur lors de l'annulation", "error");
                } finally {
                    setNotification({ isOpen: false });
                }
            }
        );
    };

    // --- ANNULATION D'UNE LIGNE DE DÉTAIL ---
    const handleAnnulerLigne = (ligneId, articleNom) => {
        showConfirm(
            `Voulez-vous annuler l'article "${articleNom}" de cette consommation ? Le stock sera recrédité.`,
            async () => {
                try {
                    await API.put(`/consommations-departements/annuler-ligne/${ligneId}`);
                    showToast("Ligne annulée avec succès et stock restauré.");
                    fetchConsommations();
                    // Recharger les détails ouverts
                    setDetailsMap({});
                    setExpandedConso({});
                } catch (err) {
                    showToast(err.response?.data?.error || "Erreur lors de l'annulation de la ligne", "error");
                } finally {
                    setNotification({ isOpen: false });
                }
            }
        );
    };

    const extractDateOnly = (dateString) => {
        if (!dateString) return '';
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return `${match[1]}-${match[2]}-${match[3]}`;
        const d = new Date(dateString);
        if (!isNaN(d.getTime())) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
        return '';
    };

    // --- FILTRAGE HISTORIQUE ---
    const consommationsFiltrees = useMemo(() => {
        return consommations.filter(item => {
            const matchSearch = 
                item.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.departement_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.utilisateur_nom?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchStatut = filterStatut === 'TOUS' || item.statut === filterStatut;

            return matchSearch && matchStatut;
        });
    }, [consommations, searchTerm, filterStatut]);

    // --- RAPPORT PAR MATIÈRE ---
    const [rapportItems, setRapportItems] = useState([]);

    useEffect(() => {
        if (activeTab === 'RAPPORT') {
            const valides = consommations.filter(c => c.statut === 'VALIDE');
            Promise.all(valides.map(c => API.get(`/consommations-departements/details/${c.id}`).catch(() => ({ data: { lignes: [] } }))))
                .then(results => {
                    let allLignes = [];
                    results.forEach((res, index) => {
                        const conso = valides[index];
                        const lignes = res.data?.lignes || [];
                        lignes.forEach(l => {
                            allLignes.push({
                                ...l,
                                created_at: conso.created_at,
                                departement_nom: conso.departement_nom
                            });
                        });
                    });
                    setRapportItems(allLignes);
                });
        }
    }, [activeTab, consommations]);

    const rapportFiltre = useMemo(() => {
        return rapportItems.filter(item => {
            const matchSearch = item.nom_article_snap?.toLowerCase().includes(searchTerm.toLowerCase());
            if (!item.created_at) return matchSearch;
            const itemDate = extractDateOnly(item.created_at);
            if (dateDebut && itemDate < dateDebut) return false;
            if (dateFin && itemDate > dateFin) return false;
            return matchSearch;
        });
    }, [rapportItems, searchTerm, dateDebut, dateFin]);

    const rapportGroupedByMaterial = useMemo(() => {
        const map = {};
        rapportFiltre.forEach(item => {
            const key = item.nom_article_snap || 'Inconnu';
            if (!map[key]) {
                map[key] = {
                    article_nom: key,
                    type_article: item.type_article,
                    total_quantite: 0,
                    total_montant: 0,
                    nombre_sorties: 0
                };
            }
            const qte = Math.abs(parseFloat(item.quantite) || 0);
            const montant = item.montant_total !== undefined ? parseFloat(item.montant_total) : (qte * (parseFloat(item.cmp_snap) || 0));
            map[key].total_quantite += qte;
            map[key].total_montant += montant;
            map[key].nombre_sorties += 1;
        });
        return Object.values(map).sort((a, b) => b.total_quantite - a.total_quantite);
    }, [rapportFiltre]);

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? '#ef4444' : (isConfirm ? '#f59e0b' : '#10b981');

        return (
            <div style={{
                position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
                zIndex: 3000, display: 'flex', alignItems: 'center',
                background: 'white', padding: '12px 20px', borderRadius: '15px',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', border: `1px solid ${borderColor}`,
                minWidth: '400px', borderLeft: `8px solid ${color}`
            }}>
                <div style={{ flex: 1, fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>
                    {notification.message}
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => notification.onConfirm()} style={{ background: primaryColor, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>OUI, CONFIRMER</button>
                            <button onClick={() => setNotification({isOpen: false})} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>ANNULER</button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', background: '#f1f5f9', height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative' }}>
            <Sidebar />
            <NotificationToast />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                <header style={{ padding: '20px 30px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <h2 style={{ color: '#0f172a', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '12px', margin: 0, fontSize: '20px' }}>
                        <History size={28} color={primaryColor} strokeWidth={2.5} /> HISTORIQUE & RAPPORTS - CONSOMMATIONS DÉPARTEMENTS
                    </h2>
                    <button onClick={fetchConsommations} style={{ background: '#f8fafc', border: `1px solid ${borderColor}`, padding: '8px 16px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#475569' }}>
                        <RefreshCw size={16} /> Actualiser
                    </button>
                </header>

                <main style={{ flex: 1, padding: '25px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', gap: '20px' }}>
                        
                        {/* Navigation et Filtres */}
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '12px', border: `1px solid ${borderColor}` }}>
                                <button
                                    onClick={() => handleTabChange('HISTORIQUE')}
                                    style={{
                                        padding: '9px 16px', borderRadius: '9px', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
                                        background: activeTab === 'HISTORIQUE' ? '#fff' : 'transparent',
                                        color: activeTab === 'HISTORIQUE' ? primaryColor : '#64748b',
                                        border: activeTab === 'HISTORIQUE' ? `1px solid ${borderColor}` : 'none',
                                        display: 'flex', alignItems: 'center', gap: '8px'
                                    }}
                                >
                                    <FileText size={16} /> Historique des Sorties
                                </button>
                                <button
                                    onClick={() => handleTabChange('RAPPORT')}
                                    style={{
                                        padding: '9px 16px', borderRadius: '9px', fontSize: '13px', fontWeight: '800', cursor: 'pointer',
                                        background: activeTab === 'RAPPORT' ? '#fff' : 'transparent',
                                        color: activeTab === 'RAPPORT' ? primaryColor : '#64748b',
                                        border: activeTab === 'RAPPORT' ? `1px solid ${borderColor}` : 'none',
                                        display: 'flex', alignItems: 'center', gap: '8px'
                                    }}
                                >
                                    <BarChart3 size={16} /> Rapport par Période (Par Article)
                                </button>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{ position: 'relative', width: '260px' }}>
                                    <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                                    <input 
                                        type="text" 
                                        placeholder="Filtrer..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{
                                            width: '100%', padding: '9px 14px 9px 42px', borderRadius: '10px',
                                            border: `1px solid ${borderColor}`, outline: 'none', fontSize: '13px',
                                            background: '#f8fafc', fontWeight: '600', color: '#1e293b'
                                        }}
                                    />
                                </div>

                                {activeTab === 'HISTORIQUE' && (
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <Filter size={16} color="#64748b" />
                                        {['TOUS', 'VALIDE', 'ANNULE'].map((statut) => (
                                            <button
                                                key={statut}
                                                onClick={() => setFilterStatut(statut)}
                                                style={{
                                                    padding: '8px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '800', cursor: 'pointer',
                                                    background: filterStatut === statut ? '#334155' : '#f8fafc',
                                                    color: filterStatut === statut ? '#fff' : '#64748b',
                                                    border: `1px solid ${filterStatut === statut ? '#334155' : borderColor}`
                                                }}
                                            >
                                                {statut}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {activeTab === 'RAPPORT' && (
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', background: '#f8fafc', padding: '12px 16px', borderRadius: '12px', border: `1px solid ${borderColor}`, flexShrink: 0 }}>
                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#334155' }}>Période du :</span>
                                <input 
                                    type="date" 
                                    value={dateDebut} 
                                    onChange={(e) => setDateDebut(e.target.value)}
                                    style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '13px', fontWeight: '600', background: '#fff' }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: '800', color: '#334155' }}>au</span>
                                <input 
                                    type="date" 
                                    value={dateFin} 
                                    onChange={(e) => setDateFin(e.target.value)}
                                    style={{ padding: '7px 12px', borderRadius: '8px', border: `1px solid ${borderColor}`, fontSize: '13px', fontWeight: '600', background: '#fff' }}
                                />
                            </div>
                        )}

                        {/* LISTE PRINCIPALE */}
                        <div style={{ flex: 1, overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: '12px', background: '#fff' }}>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                                    <Loader2 className="animate-spin" color={primaryColor} size={40} />
                                </div>
                            ) : activeTab === 'HISTORIQUE' ? (
                                consommationsFiltrees.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#94a3b8' }}>
                                        <Package size={64} strokeWidth={1.5} />
                                        <p style={{ fontWeight: '700', marginTop: '15px', fontSize: '16px' }}>Aucune consommation trouvée</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px' }}>
                                        {consommationsFiltrees.map(conso => {
                                            const isExpanded = !!expandedConso[conso.id];
                                            const lignes = detailsMap[conso.id] || [];
                                            const isAnnule = conso.statut === 'ANNULE';

                                            return (
                                                <div key={conso.id} style={{ border: `1px solid ${borderColor}`, borderRadius: '10px', overflow: 'hidden', background: isAnnule ? '#f8fafc' : '#fff', opacity: isAnnule ? 0.7 : 1 }}>
                                                    <div 
                                                        style={{
                                                            padding: '12px 16px', background: isExpanded ? '#f8fafc' : '#fff', 
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flex: 1 }} onClick={() => toggleExpand(conso.id)}>
                                                            <div style={{ background: isAnnule ? '#fee2e2' : '#f1f5f9', color: isAnnule ? '#dc2626' : primaryColor, padding: '6px', borderRadius: '6px' }}>
                                                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                            </div>
                                                            <div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <span style={{ fontWeight: '900', color: '#1e293b', fontSize: '13.5px' }}>
                                                                        {conso.libelle}
                                                                    </span>
                                                                    <span style={{ fontWeight: '800', fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: isAnnule ? '#fee2e2' : '#dcfce7', color: isAnnule ? '#dc2626' : '#166534' }}>
                                                                        {conso.statut}
                                                                    </span>
                                                                </div>
                                                                <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                                                                    Département : <strong style={{ color: '#334155' }}>{conso.departement_nom}</strong> | Saisi par : {conso.utilisateur_nom}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                            <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>
                                                                {new Date(conso.created_at).toLocaleString()}
                                                            </span>
                                                            {!isAnnule && (
                                                                <button
                                                                    onClick={() => handleAnnulerConso(conso.id, conso.libelle)}
                                                                    title="Annuler toute la consommation"
                                                                    style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '6px 10px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}
                                                                >
                                                                    <XCircle size={14} /> Annuler Tout
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isExpanded && (
                                                        <div style={{ borderTop: `1px solid ${borderColor}`, background: '#fcfcfc', padding: '12px 16px' }}>
                                                            {conso.observation && (
                                                                <p style={{ fontSize: '11.5px', color: '#475569', fontStyle: 'italic', margin: '0 0 10px 0' }}>
                                                                    Observation : {conso.observation}
                                                                </p>
                                                            )}
                                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr style={{ textAlign: 'left', fontSize: '11px', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                                                        <th style={{ padding: '8px' }}>ARTICLE</th>
                                                                        <th style={{ padding: '8px' }}>TYPE</th>
                                                                        <th style={{ padding: '8px' }}>QTÉ CONSOMMÉE</th>
                                                                        <th style={{ padding: '8px' }}>CMP UNITAIRE</th>
                                                                        <th style={{ padding: '8px' }}>MONTANT TOTAL</th>
                                                                        <th style={{ padding: '8px', textAlign: 'right' }}>ACTION</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {lignes.map(ligne => {
                                                                        const qtePositive = Math.abs(parseFloat(ligne.quantite) || 0);
                                                                        const montantLigne = ligne.montant_total !== undefined ? parseFloat(ligne.montant_total) : (qtePositive * (parseFloat(ligne.cmp_snap) || 0));
                                                                        return (
                                                                            <tr key={ligne.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}>
                                                                                <td style={{ padding: '8px', fontWeight: '700', color: '#1e293b' }}>
                                                                                    {ligne.nom_article_snap}
                                                                                </td>
                                                                                <td style={{ padding: '8px' }}>
                                                                                    <span style={{ fontWeight: '800', color: ligne.type_article === 'MP' ? '#2563eb' : '#d97706', background: ligne.type_article === 'MP' ? '#eff6ff' : '#fef3c7', padding: '2px 8px', borderRadius: '20px', fontSize: '10px' }}>
                                                                                        {ligne.type_article}
                                                                                    </span>
                                                                                </td>
                                                                                <td style={{ padding: '8px', fontWeight: '900', color: '#1e293b' }}>
                                                                                    {qtePositive}
                                                                                </td>
                                                                                <td style={{ padding: '8px', fontWeight: '700', color: '#475569' }}>
                                                                                    {Math.round(ligne.cmp_snap || 0).toLocaleString()} FCFA
                                                                                </td>
                                                                                <td style={{ padding: '8px', fontWeight: '900', color: '#059669' }}>
                                                                                    {Math.round(montantLigne).toLocaleString()} FCFA
                                                                                </td>
                                                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                                                    {!isAnnule && (
                                                                                        <button
                                                                                            onClick={() => handleAnnulerLigne(ligne.id, ligne.nom_article_snap)}
                                                                                            title="Annuler cette ligne"
                                                                                            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: '800' }}
                                                                                        >
                                                                                            <Trash2 size={12} /> Supprimer ligne
                                                                                        </button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            ) : (
                                rapportGroupedByMaterial.length === 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px', color: '#94a3b8' }}>
                                        <Package size={64} strokeWidth={1.5} />
                                        <p style={{ fontWeight: '700', marginTop: '15px', fontSize: '16px' }}>Aucun rapport disponible pour cette période</p>
                                    </div>
                                ) : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', fontSize: '12px', color: '#64748b', borderBottom: '2px solid #f1f5f9', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 10 }}>
                                                <th style={{ padding: '14px 15px' }}>DÉSIGNATION DE L'ARTICLE</th>
                                                <th style={{ padding: '14px 15px' }}>TYPE</th>
                                                <th style={{ padding: '14px 15px' }}>NOMBRE DE SORTIES</th>
                                                <th style={{ padding: '14px 15px' }}>TOTAL QUANTITÉ</th>
                                                <th style={{ padding: '14px 15px' }}>MONTANT TOTAL CUMULÉ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rapportGroupedByMaterial.map((row, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f8fafc', fontSize: '13px' }}>
                                                    <td style={{ padding: '12px 15px', fontWeight: '800', color: '#1e293b' }}>
                                                        {row.article_nom}
                                                    </td>
                                                    <td style={{ padding: '12px 15px' }}>
                                                        <span style={{ fontWeight: '800', color: row.type_article === 'MP' ? '#2563eb' : '#d97706', background: row.type_article === 'MP' ? '#eff6ff' : '#fef3c7', padding: '4px 10px', borderRadius: '20px', fontSize: '11px' }}>
                                                            {row.type_article}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '700', color: '#475569' }}>
                                                        {row.nombre_sorties} fois
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '900', color: '#1e293b' }}>
                                                        {row.total_quantite.toFixed(3)}
                                                    </td>
                                                    <td style={{ padding: '12px 15px', fontWeight: '900', color: '#059669' }}>
                                                        {Math.round(row.total_montant).toLocaleString()} FCFA
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            )}
                        </div>

                    </div>
                </main>
            </div>
        </div>
    );
};

export default ConsoDepHistorique;